import {Box, CircularProgress, Stack, Typography} from '@mui/material';
import type {PDFDocumentProxy, PDFPageProxy} from 'pdfjs-dist';
import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';

type PdfJs = typeof import('pdfjs-dist');

/** Lazy loads pdf.js so it's only downloaded when a PDF is previewed */
let pdfJsPromise: Promise<PdfJs> | undefined;
function loadPdfJs() {
  if (!pdfJsPromise) {
    pdfJsPromise = import('pdfjs-dist/legacy/build/pdf.mjs').catch((error) => {
      pdfJsPromise = undefined;
      throw error;
    });
  }
  return pdfJsPromise;
}

/** Multiplies the display pixel ratio to control PDF preview sharpness. */
const PDF_PREVIEW_SCALE_FACTOR = 1.5;

/** Max device pixel ratio used to render pages (keeps memory usage in check) */
const MAX_PIXEL_RATIO = 5;

/** Default page aspect ratio (A4 portrait) used while a page size is unknown */
const DEFAULT_PAGE_ASPECT_RATIO = 210 / 297;

type PdfViewerProps = {
  /** PDF binary data */
  data: ArrayBuffer;
};

/**
 * Renders a PDF with pdf.js (pages are drawn to images), so it looks and works the same in every
 * browser, including the ones without a built-in inline PDF viewer (e.g. Chrome on Android/TWA).
 */
export function PdfViewer({data}: PdfViewerProps) {
  const {t} = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [doc, setDoc] = useState<PDFDocumentProxy>();
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let worker: Worker | undefined;
    let destroy: (() => Promise<void>) | undefined;

    setError(false);

    (async () => {
      const pdfJs = await loadPdfJs();
      if (cancelled) return;

      // Dedicated worker per document, terminated on cleanup
      worker = new Worker(new URL('./pdf.worker.ts', import.meta.url), {type: 'module'});
      const pdfWorker: InstanceType<PdfJs['PDFWorker']> = pdfJs.PDFWorker.create({port: worker});
      // pdf.js transfers the data to the worker, so pass a copy to keep the original intact
      const loadingTask = pdfJs.getDocument({
        data: new Uint8Array(data.slice(0)),
        worker: pdfWorker,
      });
      destroy = async () => {
        await loadingTask.destroy();
        pdfWorker.destroy();
      };

      const loadedDoc = await loadingTask.promise;
      if (!cancelled) setDoc(loadedDoc);
    })().catch((err) => {
      if (cancelled) return;
      console.error('Failed to load PDF preview', err);
      setError(true);
    });

    return () => {
      cancelled = true;
      setDoc(undefined);
      const currentWorker = worker;
      (destroy?.() ?? Promise.resolve())
        .catch(() => undefined)
        .finally(() => currentWorker?.terminate());
    };
  }, [data]);

  if (error) {
    return (
      <Stack sx={{height: '100%'}} justifyContent="center" alignItems="center">
        <Typography variant="h6">{t(translations.pdf_viewer.error)}</Typography>
      </Stack>
    );
  }

  if (!doc) {
    return (
      <Stack sx={{height: '100%'}} justifyContent="center" alignItems="center">
        <Typography variant="h6">{t(translations.loading_content.loading)}</Typography>
      </Stack>
    );
  }

  return (
    <Box
      ref={scrollRef}
      sx={{
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        backgroundColor: 'grey.300',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
      }}
    >
      {Array.from({length: doc.numPages}, (_, i) => (
        <PdfPage key={i} doc={doc} pageNumber={i + 1} scrollRef={scrollRef} />
      ))}
    </Box>
  );
}

type PdfPageProps = {
  doc: PDFDocumentProxy;
  pageNumber: number;
  scrollRef: React.RefObject<HTMLDivElement>;
};

/** A single PDF page, rendered only when it gets near the visible area */
function PdfPage({doc, pageNumber, scrollRef}: PdfPageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState<PDFPageProxy>();
  const [visible, setVisible] = useState(false);
  const [src, setSrc] = useState<string>();

  // Load page (needed for its size)
  useEffect(() => {
    let cancelled = false;
    doc
      .getPage(pageNumber)
      .then((p) => !cancelled && setPage(p))
      .catch((err) => console.error(`Failed to load PDF page ${pageNumber}`, err));
    return () => {
      cancelled = true;
    };
  }, [doc, pageNumber]);

  // Detect when the page gets near the visible area
  useEffect(() => {
    const node = containerRef.current;
    if (!node || visible) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setVisible(true);
      },
      {root: scrollRef.current, rootMargin: '100% 0px'}
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [scrollRef, visible]);

  // Render page to an image
  useEffect(() => {
    const node = containerRef.current;
    if (!page || !visible || !node) return;

    let cancelled = false;
    let url: string | undefined;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return;

    const pixelRatio = Math.min(
      (window.devicePixelRatio || 1) * PDF_PREVIEW_SCALE_FACTOR,
      MAX_PIXEL_RATIO
    );
    const baseViewport = page.getViewport({scale: 1});
    const viewport = page.getViewport({
      scale: (node.clientWidth * pixelRatio) / baseViewport.width,
    });
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);

    const renderTask = page.render({canvas, canvasContext: context, viewport});
    renderTask.promise
      .then(() => new Promise<Blob | null>((resolve) => canvas.toBlob(resolve)))
      .then((blob) => {
        if (cancelled || !blob) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch((err) => {
        if (!cancelled) console.error(`Failed to render PDF page ${pageNumber}`, err);
      })
      .finally(() => {
        // Release canvas memory
        canvas.width = 0;
        canvas.height = 0;
      });

    return () => {
      cancelled = true;
      renderTask.cancel();
      if (url) URL.revokeObjectURL(url);
      setSrc(undefined);
    };
  }, [page, pageNumber, visible]);

  const viewport = page?.getViewport({scale: 1});
  const aspectRatio = viewport ? viewport.width / viewport.height : DEFAULT_PAGE_ASPECT_RATIO;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: '1000px',
        flexShrink: 0,
        aspectRatio: `${aspectRatio}`,
        backgroundColor: 'common.white',
        boxShadow: 2,
      }}
    >
      {src ? (
        <Box
          component="img"
          src={src}
          alt={`${pageNumber}`}
          sx={{display: 'block', width: '100%', height: '100%'}}
        />
      ) : (
        <Stack sx={{position: 'absolute', inset: 0}} justifyContent="center" alignItems="center">
          <CircularProgress size={32} />
        </Stack>
      )}
    </Box>
  );
}
